/**
 * Local Storage Service for Startup Journey
 * Manages saving and retrieving journey data including selected ideas and validation surveys
 */

import { BusinessIdea } from '../types/ai';
import { ValidationStepData } from '../types/validation';
import { IdeaGenerationStepData } from '../components/journey/SkilledMakerJourney';

const STORAGE_KEYS = {
  SELECTED_IDEAS: 'journey_selected_ideas',
  IDEA_GENERATION_DATA: 'journey_idea_generation_data',
  VALIDATION_DATA: 'journey_validation_data',
  CURRENT_STEP: 'journey_current_step',
  LAST_SAVED: 'journey_last_saved',
  DEFINITION_OF_DONE_PREFIX: 'journey_dod_',
  LAUNCH_ASSETS: 'journey_launch_assets'
} as const;

export class JourneyLocalStorageService {
  /**
   * Save selected ideas to local storage
   */
  static saveSelectedIdeas(ideas: BusinessIdea[]): void {
    try {
      localStorage.setItem(STORAGE_KEYS.SELECTED_IDEAS, JSON.stringify(ideas));
      this.updateLastSaved();
    } catch (error) {
      console.error('Error saving selected ideas to localStorage:', error);
    }
  }

  /**
   * Get selected ideas from local storage
   */
  static getSelectedIdeas(): BusinessIdea[] | null {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.SELECTED_IDEAS);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error retrieving selected ideas from localStorage:', error);
      return null;
    }
  }

  /**
   * Save idea generation data to local storage
   */
  static saveIdeaGenerationData(data: IdeaGenerationStepData): void {
    try {
      const serializedData = {
        ...data,
        completedAt: data.completedAt ? data.completedAt.toISOString() : undefined
      };
      localStorage.setItem(STORAGE_KEYS.IDEA_GENERATION_DATA, JSON.stringify(serializedData));
      this.updateLastSaved();
    } catch (error) {
      console.error('Error saving idea generation data to localStorage:', error);
    }
  }

  /**
   * Get idea generation data from local storage
   */
  static getIdeaGenerationData(): IdeaGenerationStepData | null {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.IDEA_GENERATION_DATA);
      if (!data) return null;

      const parsed = JSON.parse(data);
      return {
        ...parsed,
        completedAt: new Date(parsed.completedAt)
      };
    } catch (error) {
      console.error('Error retrieving idea generation data from localStorage:', error);
      return null;
    }
  }

  /**
   * Save validation data to local storage
   */
  static saveValidationData(data: ValidationStepData): void {
    try {
      const serializedData = {
        ...data,
        completedAt: data.completedAt ? data.completedAt.toISOString() : undefined,
        responses: data.responses.map(r => ({
          ...r,
          timestamp: r.timestamp.toISOString()
        }))
      };
      localStorage.setItem(STORAGE_KEYS.VALIDATION_DATA, JSON.stringify(serializedData));
      this.updateLastSaved();
    } catch (error) {
      console.error('Error saving validation data to localStorage:', error);
    }
  }

  /**
   * Get validation data from local storage
   */
  static getValidationData(): ValidationStepData | null {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.VALIDATION_DATA);
      if (!data) return null;

      const parsed = JSON.parse(data);
      return {
        ...parsed,
        completedAt: parsed.completedAt ? new Date(parsed.completedAt) : undefined,
        responses: parsed.responses.map((r: any) => ({
          ...r,
          timestamp: new Date(r.timestamp)
        }))
      };
    } catch (error) {
      console.error('Error retrieving validation data from localStorage:', error);
      return null;
    }
  }

  /**
   * Save current step to local storage
   */
  static saveCurrentStep(step: string): void {
    try {
      localStorage.setItem(STORAGE_KEYS.CURRENT_STEP, step);
      this.updateLastSaved();
    } catch (error) {
      console.error('Error saving current step to localStorage:', error);
    }
  }

  /**
   * Get current step from local storage
   */
  static getCurrentStep(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEYS.CURRENT_STEP);
    } catch (error) {
      console.error('Error retrieving current step from localStorage:', error);
      return null;
    }
  }

  /**
   * Save Definition of Done criteria for a step
   */
  static saveDefinitionOfDone(stepName: string, criteria: any[]): void {
    try {
      const key = `${STORAGE_KEYS.DEFINITION_OF_DONE_PREFIX}${stepName}`;
      localStorage.setItem(key, JSON.stringify(criteria));
      this.updateLastSaved();
    } catch (error) {
      console.error(`Error saving Definition of Done for ${stepName}:`, error);
    }
  }

  /**
   * Get Definition of Done criteria for a step
   */
  static getDefinitionOfDone(stepName: string): any[] | null {
    try {
      const key = `${STORAGE_KEYS.DEFINITION_OF_DONE_PREFIX}${stepName}`;
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error(`Error retrieving Definition of Done for ${stepName}:`, error);
      return null;
    }
  }

  /**
   * Update last saved timestamp
   */
  private static updateLastSaved(): void {
    try {
      localStorage.setItem(STORAGE_KEYS.LAST_SAVED, new Date().toISOString());
    } catch (error) {
      console.error('Error updating last saved timestamp:', error);
    }
  }

  /**
   * Get last saved timestamp
   */
  static getLastSaved(): Date | null {
    try {
      const timestamp = localStorage.getItem(STORAGE_KEYS.LAST_SAVED);
      return timestamp ? new Date(timestamp) : null;
    } catch (error) {
      console.error('Error retrieving last saved timestamp:', error);
      return null;
    }
  }

  /**
   * Clear all journey data from local storage
   */
  static clearAllJourneyData(): void {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      console.log('All journey data cleared from localStorage');
    } catch (error) {
      console.error('Error clearing journey data from localStorage:', error);
    }
  }

  /**
   * Check if there's any saved journey data
   */
  static hasSavedData(): boolean {
    try {
      return Object.values(STORAGE_KEYS).some(key => 
        localStorage.getItem(key) !== null
      );
    } catch (error) {
      console.error('Error checking for saved journey data:', error);
      return false;
    }
  }

  /**
   * Save launch assets to local storage
   */
  static saveLaunchAssets(assets: any[], projectId?: string): void {
    try {
      const data = {
        assets,
        projectId,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEYS.LAUNCH_ASSETS, JSON.stringify(data));
      this.updateLastSaved();
      
      // Calculate and log storage size
      const sizeInBytes = new Blob([JSON.stringify(data)]).size;
      const sizeInKB = (sizeInBytes / 1024).toFixed(2);
      console.log(`✅ Launch assets saved to localStorage (${sizeInKB} KB)`);
    } catch (error) {
      console.error('Error saving launch assets to localStorage:', error);
    }
  }

  /**
   * Get launch assets from local storage
   */
  static getLaunchAssets(projectId?: string): any[] | null {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.LAUNCH_ASSETS);
      if (!data) return null;

      const parsed = JSON.parse(data);
      
      // If projectId is provided, only return assets for that project
      if (projectId && parsed.projectId !== projectId) {
        console.log(`⚠️ Stored assets are for different project (stored: ${parsed.projectId}, requested: ${projectId})`);
        return null;
      }
      
      return parsed.assets;
    } catch (error) {
      console.error('Error retrieving launch assets from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear launch assets from local storage
   */
  static clearLaunchAssets(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.LAUNCH_ASSETS);
      console.log('Launch assets cleared from localStorage');
    } catch (error) {
      console.error('Error clearing launch assets from localStorage:', error);
    }
  }

  /**
   * Get all saved journey data at once
   */
  static getAllJourneyData() {
    return {
      selectedIdeas: this.getSelectedIdeas(),
      ideaGenerationData: this.getIdeaGenerationData(),
      validationData: this.getValidationData(),
      currentStep: this.getCurrentStep(),
      lastSaved: this.getLastSaved(),
      launchAssets: this.getLaunchAssets()
    };
  }
}
